import { feeProgrammes } from "../../Tuition Fees/feeData";
import { programmes as catalogueProgrammes } from "../../Tuition Fees/programmes";
import { slugify } from "../../Programmes Page/filterRoutes";

/**
 * Rule based knowledge engine for Techizen.
 * No external AI service is used - answers are matched from
 * the site's own programme / fee data and curated facts.
 */

const money = (value) =>
  `KES ${Number(value || 0).toLocaleString("en-KE")}`;

const includesAny = (text, words) =>
  words.some((word) => text.includes(word));

const normalizeTitle = (value = "") =>
  String(value).toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const canonicalSlugByTitle = new Map(
  catalogueProgrammes.map((p) => [normalizeTitle(p.title), p.slug])
);

/**
 * Resolve the canonical /programme/:slug for a fee programme.
 * Fee data has no slugs of its own, so we match against the
 * canonical programme catalog (exact title, then fuzzy),
 * falling back to the site-wide slugify helper.
 */
const resolveProgrammeSlug = (programme) => {
  if (programme.slug) return programme.slug;

  const exact = canonicalSlugByTitle.get(normalizeTitle(programme.title));
  if (exact) return exact;

  const words = normalizeTitle(programme.title).split(" ").filter(Boolean);
  if (words.length) {
    const hit = catalogueProgrammes.find((p) => {
      const title = normalizeTitle(p.title);
      return words.every((word) => title.includes(word));
    });
    if (hit) return hit.slug;
  }

  return slugify(programme.title);
};

const programmeLink = (programme) => ({
  label: programme.title,
  to: `/programme/${resolveProgrammeSlug(programme)}`,
});

const escapeRegExp = (value) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const searchProgrammes = (query) => {
  const term = query.trim().toLowerCase();
  if (term.length < 3) return [];

  const words = term
    .split(/\s+/)
    .filter((word) => word.length > 2 && !STOP_WORDS.has(word));

  if (!words.length) return [];

  // Word-boundary matching so "vision" does not match "television".
  const patterns = words.map(
    (word) => new RegExp(`\\b${escapeRegExp(word)}\\b`)
  );

  return feeProgrammes
    .map((programme) => {
      const haystack = `${programme.title} ${programme.school} ${programme.qualification} ${programme.code}`.toLowerCase();
      const score = patterns.reduce(
        (total, pattern) => (pattern.test(haystack) ? total + 1 : total),
        0
      );
      return { programme, score };
    })
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || a.programme.title.localeCompare(b.programme.title))
    .slice(0, 6)
    .map((row) => row.programme);
};

const STOP_WORDS = new Set([
  "the", "and", "for", "you", "your", "with", "what", "which", "does",
  "how", "much", "cost", "about", "tell", "can", "are", "there", "have",
  "want", "study", "studying", "course", "courses", "programme", "programmes",
  "fee", "fees", "tuition", "price", "apply", "application", "intake", "campus",
  "program", "programs", "please", "need", "info", "information", "know",
  "zetech", "university", "mission",
]);

const FACTS = [
  {
    id: "apply",
    keywords: ["apply", "application", "admission", "admissions", "enroll", "enrol", "register", "join"],
    text:
      "Applying to Zetech is simple:\n\n1. Choose your programme\n2. Fill in the online application form\n3. Upload your KCSE / academic documents\n4. Pay the application fee\n5. Wait for your admission letter\n\nYou can also walk into any of our campuses and the admissions team will help you.",
    links: [
      { label: "Start an application", to: "/application-process" },
      { label: "Browse programmes", to: "/programmes" },
    ],
  },
  {
    id: "fees",
    keywords: ["fee", "fees", "tuition", "cost", "price", "pay", "payment", "how much"],
    text:
      "Tuition depends on the programme and level of study. You can use our Tuition Fee Calculator to get an exact figure per semester and for the full programme, or compare several programmes side by side.\n\nTell me a programme name (e.g. \"fees for computer science\") and I'll give you the amount right away.",
    links: [
      { label: "Tuition fees & calculator", to: "/tuition-fees" },
      { label: "How to pay fees", to: "/fee-payments" },
    ],
  },
  {
    id: "intake",
    keywords: ["intake", "intakes", "semester", "start", "january", "may", "september", "when do"],
    text:
      "Zetech University has three intakes every year:\n\n• January Intake\n• May Intake\n• September Intake\n\nApplications stay open throughout the year, so you can apply for the next available intake at any time.",
    links: [{ label: "Application process", to: "/application-process" }],
  },
  {
    id: "campus",
    keywords: ["campus", "campuses", "location", "located", "where", "ruiru", "mangu", "town"],
    text:
      "Our learning centers are:\n\n• Ruiru Main Campus\n• Technology Park Mang'u Campus\n• Zetech Digital School (online)\n\nEach offers student-centred facilities, learning support and a vibrant student experience.",
    links: [{ label: "See our campuses", to: "/our-campuses" }],
  },
  {
    id: "accommodation",
    keywords: ["accommodation", "hostel", "hostels", "housing", "stay", "room"],
    text:
      "Zetech works with vetted accommodation providers close to our campuses so you get safe, affordable and comfortable housing while you study.",
    links: [{ label: "Student accommodation", to: "/student-accommodation" }],
  },
  {
    id: "international",
    keywords: ["international", "visa", "foreign", "abroad", "permit", "immigration"],
    text:
      "International students are very welcome at Zetech. We support you with admission, student pass / visa guidance, orientation and settling in Kenya.",
    links: [{ label: "International students", to: "/international-students" }],
  },
  {
    id: "exams",
    keywords: ["exam", "exams", "memo", "memos", "timetable", "results"],
    text:
      "Examination memos, schedules and related notices are published on our Exam Memos page and updated every semester.",
    links: [{ label: "Exam memos", to: "/exam-memos" }],
  },
  {
    id: "schools",
    keywords: ["school", "schools", "faculty", "faculties", "department", "ict", "business", "hospitality", "media"],
    text:
      "Our academic schools include:\n\n• School of ICT, Media and Engineering\n• School of Business and Economics\n• School of Hospitality, Tourism and Creative Arts\n• School of Education, Arts and Social Sciences\n• Institute of Professional Studies / TVET\n\nWhich school interests you?",
    links: [{ label: "Our schools & programmes", to: "/programmes" }],
  },
  {
    id: "contact",
    keywords: ["contact", "phone", "call", "email", "talk", "speak", "help desk", "reach"],
    text:
      "You can reach the admissions team on:\n\n📞 0719 034 500 / 0706622557\n✉️ info@zetech.ac.ke\n\nOr use the WhatsApp button on this page for an instant chat with a human.",
    links: [{ label: "Frequently asked questions", to: "/faqs" }],
  },
  {
    id: "scholarship",
    keywords: ["scholarship", "bursary", "helb", "sponsor", "funding", "discount"],
    text:
      "Zetech supports students through HELB loans, flexible fee payment plans and merit based awards. The finance office can guide you on the option that fits you best.",
    links: [{ label: "Fee payment options", to: "/fee-payments" }],
  },
  {
    id: "life",
    keywords: ["student life", "clubs", "sports", "life", "activities", "events"],
    text:
      "Life at Zetech goes beyond class - clubs, sports, talent shows, innovation hubs, mentorship and industry events run all year round.",
    links: [
      { label: "Student life", to: "/students-life" },
      { label: "Upcoming events", to: "/events" },
    ],
  },
  {
    id: "about",
    keywords: ["about", "history", "who are you", "zetech university", "ranked", "accredit", "charter"],
    text:
      "Zetech University is a chartered private university in Kenya committed to technology driven, industry relevant education.",
    links: [
      { label: "About Zetech", to: "/about-zu" },
      { label: "Mission, vision & philosophy", to: "/mission-vision" },
      { label: "Charter & accreditation", to: "/charter" },
    ],
  },
  {
    id: "news",
    keywords: ["news", "blog", "blogs", "article", "articles", "stories", "updates", "latest"],
    text:
      "Catch up with the latest Zetech news, stories and campus updates on our blog - admissions announcements, student success stories and university events.",
    links: [
      { label: "Latest news & articles", to: "/blog" },
      { label: "Upcoming events", to: "/events" },
    ],
  },
  {
    id: "research",
    keywords: ["research", "publication", "publications", "journal", "conference", "conferences", "call for papers"],
    text:
      "Zetech runs an active research agenda with conferences, publications and industry collaborations. Explore ongoing work or join an upcoming research conference.",
    links: [
      { label: "Research at Zetech", to: "/research" },
      { label: "Conferences", to: "/conferences" },
    ],
  },
  {
    id: "innovation",
    keywords: ["innovation", "innovations", "izethub", "hub", "startup", "startups", "entrepreneurship", "incubation"],
    text:
      "iZetHub is our innovation and entrepreneurship ecosystem - incubation, mentorship, hackathons and industry linkages that turn student ideas into ventures.",
    links: [{ label: "Innovation & iZetHub", to: "/innovations" }],
  },
  {
    id: "ageing",
    keywords: ["ageing", "aging", "gerontology", "geriatrics", "older persons", "elderly", "ias", "kinuthia", "ageing studies", "old age", "seniors"],
    text:
      "The Institute of Ageing Studies (IAS) is a partnership between Zetech University and the Kinuthia Wa Mwangi Foundation. It offers specialized education, training, research and consultancy in gerontology and geriatrics - from short courses to postgraduate degrees - through its Gerontology, Geriatrics, and Research, Consultancy & Development units.",
    links: [
      { label: "Institute of Ageing Studies", to: "/institutes/ias" },
      { label: "Our institutes", to: "/institutes" },
    ],
  },
  {
    id: "agritech",
    keywords: ["agritech", "agriculture", "farming", "farm", "agribusiness", "poultry", "dairy", "pig", "rabbit", "avocado", "goat", "bee keeping", "fish farming", "drone", "greenhouse", "animal nutrition", "feed formulation", "agri"],
    text:
      "The Zetech AgriTech and Innovation Hub offers hands-on professional short courses - animal nutrition, poultry, dairy, pig, rabbit and avocado farming, drones and AI in agriculture - plus agribusiness incubation at Technology Park, Mang'u Campus. Most cohorts cost KES 10,000 with a certificate on completion. Enquiries: 0714 588 869 or 0743 830 180.",
    links: [
      { label: "AgriTech and Innovation Hub", to: "/agritech-innovation-hub" },
      { label: "Our institutes", to: "/institutes" },
    ],
  },
  {
    id: "cpc",
    keywords: ["cpc", "certification", "certifications", "professional certification", "icdl", "cisco", "ccna", "huawei", "computer packages", "graphic design", "digital marketing", "quickbooks", "sage", "spss", "autocad", "archicad", "web design", "python", "java programming", "php", "wordpress", "mysql", "database management", "french", "english course", "pastry", "pastries", "cake making", "hospitality", "solar pv", "electrical wireman", "pearson vue", "cybersecurity"],
    text:
      "The Center for Professional Certifications (CPC) offers skill-based professional short courses - ICDL, Cisco (CCNA, CyberOps, A+) and Huawei certifications, computer packages, graphic design, web design and programming, accounting packages (QuickBooks, Sage Evolution), data analysis with SPSS, languages (English and French), social sciences and hospitality - ranging from 2 weeks to 6 months. Zetech is also a Pearson VUE Authorised Test Center. Enquiries: 0719 034 500, 0706 622 557 or courses@zetech.ac.ke.",
    links: [
      { label: "Center for Professional Certifications", to: "/professional-certifications" },
      { label: "Our institutes", to: "/institutes" },
    ],
  },
  {
    id: "institutes",
    keywords: ["institute", "institutes", "tvet", "professional courses", "short course", "short courses", "certification"],
    text:
      "Our institutes and TVET programmes offer professional, market-driven short courses and certifications alongside degree pathways.",
    links: [
      { label: "Our institutes", to: "/institutes" },
      { label: "All programmes", to: "/programmes" },
    ],
  },
  {
    id: "foundation",
    keywords: ["foundation", "charity", "csr", "community", "give back", "corporate social responsibility"],
    text:
      "Through the Zetech Foundation and our CSR programmes we invest in communities - scholarships, outreach and sustainable impact projects.",
    links: [
      { label: "Zetech Foundation", to: "/our-foundation" },
      { label: "CSR initiatives", to: "/corporate-social-responsibility" },
    ],
  },
  {
    id: "governance",
    keywords: ["governance", "council", "senate", "management", "leadership", "vice chancellor", "structure"],
    text:
      "Learn about the university's governance structure - the council, senate and leadership teams that steer Zetech.",
    links: [{ label: "University governance", to: "/governance" }],
  },
  {
    id: "quality",
    keywords: ["quality", "iso", "quality policy", "standards", "certification"],
    text:
      "Zetech is ISO certified and runs a rigorous quality management system across teaching, research and service delivery.",
    links: [{ label: "Quality policy", to: "/quality-policy" }],
  },
  {
    id: "testimonials",
    keywords: ["testimonial", "testimonials", "alumni", "review", "reviews", "graduate stories", "success stories"],
    text:
      "Hear directly from our students and alumni - their journeys, careers and what studying at Zetech is really like.",
    links: [{ label: "Student testimonials", to: "/testimonials" }],
  },
  {
    id: "servicecharter",
    keywords: ["service charter", "service standards", "turnaround", "complaint", "complaints"],
    text:
      "Our Service Charter spells out the service standards and turnaround times you can expect from every Zetech office, plus how to raise feedback or complaints.",
    links: [{ label: "Service charter", to: "/service-charter" }],
  },
  {
    id: "anthem",
    keywords: ["anthem", "school song", "university song"],
    text:
      "Yes - Zetech University has its own anthem. Listen to it and read the lyrics on the anthem page.",
    links: [{ label: "Zetech anthem", to: "/zetech-anthem" }],
  },
  {
    id: "mission",
    keywords: ["mission", "vision", "philosophy", "core values", "values"],
    text:
      "Our mission, vision and philosophy capture what drives Zetech through accessible, technology-driven education.",
    links: [{ label: "Mission, vision & philosophy", to: "/mission-vision" }],
  },
];

const GREETINGS = ["hi", "hey", "hello", "habari", "sasa", "niaje", "good morning", "good afternoon", "good evening"];

export const QUICK_ACTIONS = [
  "Find a programme",
  "Tuition fees",
  "How to apply",
  "Intakes",
  "Campuses",
  "Contact admissions",
];

export const WHATSAPP_NUMBER = "254706622557";

export const buildWhatsAppLink = (context = "") => {
  const message = context
    ? `Hello Zetech University. I was chatting with Techizen AI about: "${context}". I'd like to talk to an agent.`
    : "Hello Zetech University. I'd like to talk to an agent.";

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
};

const DEFAULT_SUGGESTIONS = [
  "Tuition fees",
  "How to apply",
  "Next intake",
  "Talk to an agent",
];

export const WELCOME_MESSAGE = {
  type: "text",
  text:
    "👋 Hi, I'm Techizen AI - your Zetech University assistant.\n\nI can help with programmes, tuition fees, intakes, admissions, campuses and more.\n\nWhat would you like to know?",
  suggestions: [
    "Find a programme",
    "Tuition fees",
    "How to apply",
    "Talk to an agent",
  ],
};

export function getBotResponse(rawInput = "") {
  const input = rawInput.trim();
  const text = input.toLowerCase();

  if (!text) {
    return { type: "text", text: "Type a question and I'll do my best to help.", suggestions: DEFAULT_SUGGESTIONS };
  }

  // Escalation to a human agent on WhatsApp
  if (
    includesAny(text, [
      "agent", "human", "person", "someone", "staff", "advisor", "adviser",
      "whatsapp", "call me", "talk to", "speak to", "customer care", "support team",
    ])
  ) {
    return {
      type: "text",
      text:
        "Sure - I can hand you over to our team 👩🏽‍💼\n\nTap the button below to continue the conversation on WhatsApp. Our admissions agents reply Monday to Friday, 8:00am - 5:00pm (and Saturday mornings).",
      whatsapp: input,
      suggestions: ["Tuition fees", "How to apply", "Campuses"],
    };
  }

  if (GREETINGS.some((word) => text === word || text.startsWith(`${word} `))) {
    return {
      type: "text",
      text: "Hello! 😊 Ask me about programmes, fees, intakes, campuses or how to apply.",
      suggestions: DEFAULT_SUGGESTIONS,
    };
  }

  if (includesAny(text, ["thank", "asante", "thanks"])) {
    return {
      type: "text",
      text: "You're most welcome. Anything else I can help you with?",
      suggestions: DEFAULT_SUGGESTIONS,
    };
  }

  // Programme specific fee question, e.g. "fees for computer science"
  const asksFees = includesAny(text, ["fee", "fees", "tuition", "cost", "how much", "price"]);
  const matches = searchProgrammes(text);

  if (matches.length === 1 || (matches.length && asksFees)) {
    const programme = matches[0];
    const perPeriod = programme.isRecurring
      ? `${money(programme.fee)} ${programme.feeType.toLowerCase()}`
      : money(programme.fee);

    return {
      type: "text",
      text:
        `🎓 ${programme.title}\n\n` +
        `School: ${programme.school || "Zetech University"}\n` +
        `Level: ${programme.qualification || programme.level || "-"}\n` +
        `Duration: ${programme.duration || "-"} ${programme.durationType || ""}`.trim() +
        `\nFee: ${perPeriod}` +
        (programme.totalFee && programme.totalFee !== programme.fee
          ? `\nEstimated total: ${money(programme.totalFee)}`
          : ""),
      links: [
        programmeLink(programme),
        { label: "Tuition calculator", to: "/tuition-fees" },
        { label: "Apply now", to: "/application-process" },
      ],
      suggestions: [
        "Entry requirements",
        "How to apply",
        "Next intake",
        "Talk to an agent",
      ],
    };
  }

  if (matches.length > 1) {
    return {
      type: "text",
      text: `I found ${matches.length} programmes that match "${input}". Which one did you mean?`,
      links: matches.map(programmeLink),
      suggestions: ["Tuition fees", "Entry requirements", "Talk to an agent"],
    };
  }

  // Pick the fact with the most keyword matches so specific topics
  // (e.g. "research conferences") win over broad ones (e.g. "about").
  const fact = FACTS
    .map((entry) => ({
      entry,
      score: entry.keywords.reduce(
        (total, keyword) => (text.includes(keyword) ? total + 1 : total),
        0
      ),
    }))
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score)[0]?.entry;
  if (fact) {
    return {
      type: "text",
      text: fact.text,
      links: fact.links,
      suggestions: DEFAULT_SUGGESTIONS.filter(
        (item) => item.toLowerCase() !== fact.id
      ),
    };
  }

  if (includesAny(text, ["programme", "program", "course", "study", "degree", "diploma", "certificate", "masters", "phd"])) {
    return {
      type: "text",
      text:
        "We offer Certificate, Diploma, Undergraduate, Postgraduate and Professional programmes across all our schools.\n\nTell me a subject (for example \"computer science\", \"business\" or \"hospitality\") and I'll show you the matching programmes.",
      links: [{ label: "Browse all programmes", to: "/programmes" }],
      suggestions: ["Computer science", "Business", "Tuition fees", "Talk to an agent"],
    };
  }

  return {
    type: "text",
    text:
      "I'm not sure I understood that one 🤔\n\nI can help with:\n• Programmes and entry requirements\n• Tuition fees\n• Intakes and application steps\n• Campuses and student life\n\nTry rephrasing, or tap a suggestion below - or chat with a real person.",
    links: [
      { label: "Programmes", to: "/programmes" },
      { label: "Tuition fees", to: "/tuition-fees" },
      { label: "Contact us", to: "/faqs" },
    ],
    whatsapp: input,
    suggestions: DEFAULT_SUGGESTIONS,
  };
}

